import { useState, useEffect, useMemo, useCallback } from 'react';
import { Order, ProcurementBatch } from '../types';
import { usePaginatedOrders } from './usePaginatedOrders';
import { 
  getProcurementBatchesForOrders,
  preloadProcurementBatchesForOrders
} from '../services/procurementCacheService';
import {
  isOrderReadyToPack,
  getOrderCollectionStatus,
  groupOrdersByCollectionStatus,
  createProcurementBatchMap
} from '../utils/procurementUtils';

interface UsePaginatedOrdersOptions {
  pageSize?: number;
  enabled?: boolean;
}

interface OrderWithProcurementInfo extends Order {
  // Extended order properties for ready-to-pack functionality
  isReadyToPack: boolean;
  procurementBatch: ProcurementBatch | null;
  collectionStatus: {
    totalItems: number;
    fullyCollectedItems: number;
    overallPercentage: number;
    isReadyToPack: boolean;
  };
}

interface UseOrdersWithProcurementResult {
  // Inherit all properties from the original hook
  orders: OrderWithProcurementInfo[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
  searchQuery: string;
  selectedStatuses: import('../types').OrderStatus[];
  selectedPaymentStatuses: string[];
  selectedShippingMethods: string[];
  selectedDiscountCodes: string[];
  selectedPackedStatuses: string[];
  priorityProcessingFilter: boolean;
  sortField: import('../components/OrderSort').OrderSortField | null;
  sortOrder: import('../components/OrderSort').OrderSortOrder;
  isSearchMode: boolean;
  isFilterMode: boolean;
  isSortMode: boolean;
  isCombinedMode: boolean;
  searchLoading: boolean;
  filterLoading: boolean;
  sortLoading: boolean;
  
  // Original hook methods
  setSearchQuery: (query: string) => void;
  setSelectedStatuses: (statuses: import('../types').OrderStatus[]) => void;
  setSelectedPaymentStatuses: (statuses: string[]) => void;
  setSelectedShippingMethods: (methods: string[]) => void;
  setSelectedDiscountCodes: (codes: string[]) => void;
  setSelectedPackedStatuses: (statuses: string[]) => void;
  setPriorityProcessingFilter: (enabled: boolean) => void;
  setSorting: (field: import('../components/OrderSort').OrderSortField | null, order: import('../components/OrderSort').OrderSortOrder) => void;
  handleSearch: (query: string) => void;
  fetchNextPage: () => Promise<void>;
  fetchPage: (page: number) => Promise<void>;
  refresh: () => Promise<void>;
  reset: () => void;
  updateOrderInCache: (updatedOrder: Order) => void;
  removeOrderFromCache: (orderId: string) => void;
  invalidateCache: () => void;

  // New procurement-related properties
  procurementBatches: Map<string, ProcurementBatch>;
  procurementLoading: boolean;
  procurementError: string | null;
  
  // Ready-to-pack specific data
  readyToPackOrders: OrderWithProcurementInfo[];
  readyToPackCount: number;
  ordersByStatus: {
    readyToPack: OrderWithProcurementInfo[];
    partiallyCollected: OrderWithProcurementInfo[];
    notInProcurement: OrderWithProcurementInfo[];
    noProcurementBatch: OrderWithProcurementInfo[];
  };
  
  // Utility methods
  refreshProcurementData: () => Promise<void>;
  getOrderReadyToPackStatus: (order: Order) => boolean;
  getOrderCollectionInfo: (order: Order) => {
    totalItems: number;
    fullyCollectedItems: number;
    overallPercentage: number;
    isReadyToPack: boolean;
  };
}

/**
 * Enhanced orders hook that includes procurement batch data and ready-to-pack calculations
 * This wraps the existing usePaginatedOrders hook and adds procurement functionality
 */
export const useOrdersWithProcurement = (options: UsePaginatedOrdersOptions = {}): UseOrdersWithProcurementResult => {
  // Use the existing orders hook
  const orderHookResult = usePaginatedOrders(options);
  const { orders: baseOrders, loading: ordersLoading } = orderHookResult;

  // Procurement-specific state
  const [procurementBatches, setProcurementBatches] = useState<Map<string, ProcurementBatch>>(new Map());
  const [procurementLoading, setProcurementLoading] = useState(false);
  const [procurementError, setProcurementError] = useState<string | null>(null);

  // Fetch procurement batches when orders change
  const fetchProcurementBatches = useCallback(async (orders: Order[]) => {
    if (orders.length === 0) {
      setProcurementBatches(new Map());
      return;
    }

    try {
      setProcurementLoading(true);
      setProcurementError(null);

      const batchesMap = await getProcurementBatchesForOrders(orders);
      setProcurementBatches(batchesMap);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch procurement batches';
      setProcurementError(errorMessage);
      console.error('Error fetching procurement batches:', error);
    } finally {
      setProcurementLoading(false);
    }
  }, []);

  // Effect to fetch procurement data when orders change
  useEffect(() => {
    if (!ordersLoading && baseOrders.length > 0) {
      fetchProcurementBatches(baseOrders);
      
      // Preload batches for performance
      preloadProcurementBatchesForOrders(baseOrders);
    } else if (baseOrders.length === 0) {
      setProcurementBatches(new Map());
    }
  }, [baseOrders, ordersLoading, fetchProcurementBatches]);

  // Create enhanced orders with procurement information
  const ordersWithProcurement = useMemo((): OrderWithProcurementInfo[] => {
    return baseOrders.map(order => {
      const procurementBatch = order.procurementBatchId ? 
        procurementBatches.get(order.procurementBatchId) || null : null;
      
      const collectionStatus = getOrderCollectionStatus(order, procurementBatch);
      const isReady = isOrderReadyToPack(order, Array.from(procurementBatches.values()));

      return {
        ...order,
        isReadyToPack: isReady,
        procurementBatch,
        collectionStatus
      };
    });
  }, [baseOrders, procurementBatches]);

  // Calculate ready-to-pack orders
  const readyToPackOrders = useMemo(() => {
    return ordersWithProcurement.filter(order => order.isReadyToPack);
  }, [ordersWithProcurement]);

  // Group orders by collection status
  const ordersByStatus = useMemo(() => {
    const batches = Array.from(procurementBatches.values());
    const grouped = groupOrdersByCollectionStatus(baseOrders, batches);
    
    // Convert to enhanced orders
    const enhanceOrders = (orders: Order[]): OrderWithProcurementInfo[] => {
      return orders.map(order => {
        const enhanced = ordersWithProcurement.find(o => o.id === order.id);
        return enhanced || {
          ...order,
          isReadyToPack: false,
          procurementBatch: null,
          collectionStatus: {
            totalItems: order.items.length,
            fullyCollectedItems: 0,
            overallPercentage: 0,
            isReadyToPack: false
          }
        };
      });
    };

    return {
      readyToPack: enhanceOrders(grouped.readyToPack),
      partiallyCollected: enhanceOrders(grouped.partiallyCollected),
      notInProcurement: enhanceOrders(grouped.notInProcurement),
      noProcurementBatch: enhanceOrders(grouped.noProcurementBatch)
    };
  }, [baseOrders, procurementBatches, ordersWithProcurement]);

  // Utility methods
  const refreshProcurementData = useCallback(async () => {
    await fetchProcurementBatches(baseOrders);
  }, [fetchProcurementBatches, baseOrders]);

  const getOrderReadyToPackStatus = useCallback((order: Order): boolean => {
    return isOrderReadyToPack(order, Array.from(procurementBatches.values()));
  }, [procurementBatches]);

  const getOrderCollectionInfo = useCallback((order: Order) => {
    const procurementBatch = order.procurementBatchId ? 
      procurementBatches.get(order.procurementBatchId) || null : null;
    return getOrderCollectionStatus(order, procurementBatch);
  }, [procurementBatches]);

  // Enhanced updateOrderInCache that also refreshes procurement data
  const updateOrderInCache = useCallback((updatedOrder: Order) => {
    orderHookResult.updateOrderInCache(updatedOrder);
    
    // If the order's procurement status might have changed, refresh procurement data
    if (updatedOrder.inProcurement || updatedOrder.procurementBatchId) {
      setTimeout(() => refreshProcurementData(), 100);
    }
  }, [orderHookResult, refreshProcurementData]);

  return {
    // Enhanced orders with procurement information
    orders: ordersWithProcurement,
    loading: ordersLoading || procurementLoading,
    
    // Inherit all original properties except orders (which we override)
    error: orderHookResult.error,
    totalCount: orderHookResult.totalCount,
    currentPage: orderHookResult.currentPage,
    totalPages: orderHookResult.totalPages,
    hasMore: orderHookResult.hasMore,
    searchQuery: orderHookResult.searchQuery,
    selectedStatuses: orderHookResult.selectedStatuses,
    selectedPaymentStatuses: orderHookResult.selectedPaymentStatuses,
    selectedShippingMethods: orderHookResult.selectedShippingMethods,
    selectedDiscountCodes: orderHookResult.selectedDiscountCodes,
    selectedPackedStatuses: orderHookResult.selectedPackedStatuses,
    priorityProcessingFilter: orderHookResult.priorityProcessingFilter,
    sortField: orderHookResult.sortField,
    sortOrder: orderHookResult.sortOrder,
    isSearchMode: orderHookResult.isSearchMode,
    isFilterMode: orderHookResult.isFilterMode,
    isSortMode: orderHookResult.isSortMode,
    isCombinedMode: orderHookResult.isCombinedMode,
    searchLoading: orderHookResult.searchLoading,
    filterLoading: orderHookResult.filterLoading,
    sortLoading: orderHookResult.sortLoading,
    setSearchQuery: orderHookResult.setSearchQuery,
    setSelectedStatuses: orderHookResult.setSelectedStatuses,
    setSelectedPaymentStatuses: orderHookResult.setSelectedPaymentStatuses,
    setSelectedShippingMethods: orderHookResult.setSelectedShippingMethods,
    setSelectedDiscountCodes: orderHookResult.setSelectedDiscountCodes,
    setSelectedPackedStatuses: orderHookResult.setSelectedPackedStatuses,
    setPriorityProcessingFilter: orderHookResult.setPriorityProcessingFilter,
    setSorting: orderHookResult.setSorting,
    handleSearch: orderHookResult.handleSearch,
    fetchNextPage: orderHookResult.fetchNextPage,
    fetchPage: orderHookResult.fetchPage,
    refresh: orderHookResult.refresh,
    reset: orderHookResult.reset,
    removeOrderFromCache: orderHookResult.removeOrderFromCache,
    invalidateCache: orderHookResult.invalidateCache,
    updateOrderInCache,
    
    // New procurement-related properties
    procurementBatches,
    procurementLoading,
    procurementError,
    
    // Ready-to-pack specific data
    readyToPackOrders,
    readyToPackCount: readyToPackOrders.length,
    ordersByStatus,
    
    // Utility methods
    refreshProcurementData,
    getOrderReadyToPackStatus,
    getOrderCollectionInfo
  };
};